import { useState, type FormEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PostUserTag } from '../../lib/api';
import { Button, Input } from '../../components/ui';
import { POST_LIMITS, USERNAME_RE, normalizeUsername } from './limits';

/**
 * Click the photo where the person is, type their username. Positions are
 * stored as fractions of the photo's width and height — Instagram's `x`/`y`.
 *
 * The clickable box is sized to the rendered photo itself (inline-block around
 * an image capped by height), never letterboxed, so the fractions measure the
 * photo and not the empty margin around it.
 */
export function TagPeopleEditor({
  imageUrl,
  tags,
  onChange,
}: {
  imageUrl: string;
  tags: PostUserTag[];
  onChange: (tags: PostUserTag[]) => void;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null);
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isFull = tags.length >= POST_LIMITS.userTagsPerImage;

  const place = (event: MouseEvent<HTMLButtonElement>) => {
    if (isFull) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const clamp = (v: number) => Math.min(1, Math.max(0, Math.round(v * 1000) / 1000));
    // jsdom and zero-size layouts report an empty rect: fall back to the centre.
    const x = rect.width > 0 ? clamp((event.clientX - rect.left) / rect.width) : 0.5;
    const y = rect.height > 0 ? clamp((event.clientY - rect.top) / rect.height) : 0.5;
    setPending({ x, y });
    setUsername('');
    setError(null);
  };

  const add = (event: FormEvent) => {
    event.preventDefault();
    if (!pending) return;
    const name = normalizeUsername(username);
    if (!USERNAME_RE.test(name)) {
      setError(t('composerMedia.tagPeople.invalid'));
      return;
    }
    if (tags.some((tag) => tag.username === name)) {
      setError(t('composerMedia.tagPeople.already', { name }));
      return;
    }
    onChange([...tags, { username: name, ...pending }]);
    setPending(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-center rounded-(--radius-control) bg-(--color-surface-sunken)">
        <div className="relative inline-block max-w-full">
          <button
            type="button"
            onClick={place}
            aria-label={t('composerMedia.tagPeople.photo')}
            className="block cursor-crosshair disabled:cursor-not-allowed"
            disabled={isFull}
          >
            <img src={imageUrl} alt="" className="block max-h-[56vh] max-w-full" />
          </button>
          {tags.map((tag) => (
            <span
              key={tag.username}
              style={{ left: `${tag.x * 100}%`, top: `${tag.y * 100}%` }}
              className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-md bg-black/75 py-1 pl-2 pr-1 text-xs font-semibold text-white shadow"
            >
              @{tag.username}
              <button
                type="button"
                onClick={() => onChange(tags.filter((other) => other.username !== tag.username))}
                aria-label={t('composerMedia.tagPeople.removeTag', { name: tag.username })}
                className="flex size-4 items-center justify-center rounded text-[10px] hover:bg-white/20"
              >
                ✕
              </button>
            </span>
          ))}
          {pending ? (
            <span
              style={{ left: `${pending.x * 100}%`, top: `${pending.y * 100}%` }}
              className="pointer-events-none absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-(--color-accent) ring-2 ring-white"
            />
          ) : null}
        </div>
      </div>

      {pending ? (
        <form onSubmit={add} className="flex flex-wrap items-start gap-2">
          <div className="min-w-[200px] flex-1">
            <Input
              autoFocus
              aria-label={t('composerMedia.tagPeople.username')}
              placeholder={t('composerFields.usernamePlaceholder')}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
            {error ? <p className="mt-1 text-xs text-(--color-danger-text)">{error}</p> : null}
          </div>
          <Button type="submit">{t('composerMedia.tagPeople.submit')}</Button>
          <Button type="button" variant="ghost" onClick={() => setPending(null)}>
            {t('composerMedia.tagPeople.cancel')}
          </Button>
        </form>
      ) : (
        <p className="text-xs text-(--color-ink-500)">
          {isFull ? t('composerMedia.tagPeople.full', { max: POST_LIMITS.userTagsPerImage }) : t('composerMedia.tagPeople.help')}
        </p>
      )}
    </div>
  );
}
